import React, { useState } from 'react';
import { Settings, CheckCircle, AlertTriangle, Save, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const CheckoutPolicySettings = ({
  policy,
  onSave,
  loading = false
}) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState(policy);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    console.log(section, field, value);
    console.log(formData);
    
  };

  const handleSave = () => {
    console.log(formData);
    const updatePolicy = {
      earlyCheckoutRules: {
        enabled: formData.earlyCheckoutRules.enabled,
        minimumHoursEarly: formData.earlyCheckoutRules.minimumHoursEarly,
        refundPercentage: formData.earlyCheckoutRules.refundPercentage,
        processingFee: formData.earlyCheckoutRules.processingFee
      },
      lateCheckoutRules: {
        enabled: formData.lateCheckoutRules.enabled,
        gracePeriodMinutes: formData.lateCheckoutRules.gracePeriodMinutes,
        hourlyRate: formData.lateCheckoutRules.hourlyRate,
        maximumHours: formData.lateCheckoutRules.maximumHours,
        fullDayChargeAfter: formData.lateCheckoutRules.fullDayChargeAfter
      },
      isActive: formData.isActive
    }
    onSave(updatePolicy);
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
      <h3 className="text-lg font-semibold text-slate-900 mb-6 flex items-center space-x-2 rtl:space-x-reverse">
        <Settings size={20} className="text-blue-600" />
        <span>{t('policySettings')}</span>
      </h3>

      <div className="space-y-8">
        {/* Early Checkout Rules */}
        <div className="bg-green-50 rounded-xl p-6 border border-green-200">
          <h4 className="text-lg font-medium text-green-900 mb-4 flex items-center space-x-2 rtl:space-x-reverse">
            <CheckCircle size={18} />
            <span>{t('earlyCheckoutRules')}</span>
          </h4>

          <div className="space-y-4">
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <input
                type="checkbox"
                checked={formData.earlyCheckoutRules.enabled}
                onChange={(e) => handleInputChange('earlyCheckoutRules', 'enabled', e.target.checked)}
                className="w-4 h-4 text-green-600 border-slate-300 rounded focus:ring-green-500"
                disabled={loading}
              />
              <label className="text-sm font-medium text-slate-700">
                {t('enableEarlyCheckout')}
              </label>
            </div>

            {formData.earlyCheckoutRules.enabled && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('minimumHoursEarly')}
                  </label>
                  <input
                    type="number"
                    value={formData.earlyCheckoutRules.minimumHoursEarly}
                    onChange={(e) => handleInputChange('earlyCheckoutRules', 'minimumHoursEarly', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                    min="1"
                    max="24"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('refundPercentage')}
                  </label>
                  <input
                    type="number"
                    value={formData.earlyCheckoutRules.refundPercentage}
                    onChange={(e) => handleInputChange('earlyCheckoutRules', 'refundPercentage', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                    min="0"
                    max="100"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('processingFee')} ({t('EGP')})
                  </label>
                  <input
                    type="number"
                    value={formData.earlyCheckoutRules.processingFee}
                    onChange={(e) => handleInputChange('earlyCheckoutRules', 'processingFee', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                    min="0"
                    disabled={loading}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Late Checkout Rules */}
        <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
          <h4 className="text-lg font-medium text-orange-900 mb-4 flex items-center space-x-2 rtl:space-x-reverse">
            <AlertTriangle size={18} />
            <span>{t('lateCheckoutRules')}</span>
          </h4>

          <div className="space-y-4">
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <input
                type="checkbox"
                checked={formData.lateCheckoutRules.enabled}
                onChange={(e) => handleInputChange('lateCheckoutRules', 'enabled', e.target.checked)}
                className="w-4 h-4 text-orange-600 border-slate-300 rounded focus:ring-orange-500"
                disabled={loading}
              />
              <label className="text-sm font-medium text-slate-700">
                {t('enableLateCheckout')}
              </label>
            </div>

            {formData.lateCheckoutRules.enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('gracePeriodMinutes')}
                  </label>
                  <input
                    type="number"
                    value={formData.lateCheckoutRules.gracePeriodMinutes}
                    onChange={(e) => handleInputChange('lateCheckoutRules', 'gracePeriodMinutes', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                    min="0"
                    max="180"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('hourlyRate')} ({t('EGP')})
                  </label>
                  <input
                    type="number"
                    value={formData.lateCheckoutRules.hourlyRate}
                    onChange={(e) => handleInputChange('lateCheckoutRules', 'hourlyRate', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                    min="0"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('maximumHours')}
                  </label>
                  <input
                    type="number"
                    value={formData.lateCheckoutRules.maximumHours}
                    onChange={(e) => handleInputChange('lateCheckoutRules', 'maximumHours', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                    min="1"
                    max="24"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('fullDayChargeAfter')}
                  </label>
                  <input
                    type="number"
                    value={formData.lateCheckoutRules.fullDayChargeAfter}
                    onChange={(e) => handleInputChange('lateCheckoutRules', 'fullDayChargeAfter', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                    min="1"
                    max="24"
                    disabled={loading}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center space-x-2 rtl:space-x-reverse px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader size={20} className="animate-spin" /> : <Save size={20} />}
            <span>{loading ? t('loading') : t('Save Changes')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};